// Sprint E · Task 2 — explicit thresholds per distance field.
// Single source of truth so both AnalysisPanel + ProjectsPage classify
// the same value the same way. All thresholds are documented inline so
// a reviewer can audit "why is 522 m amber?" → "because residential warn
// threshold is 2000 m, bad below 500 m".

// direction: "far_is_good" — bigger distance is better (residential, hazards)
// direction: "near_is_good" — smaller distance is better (grid, road, IXP)
//
// Buckets: good > warn > bad. For far_is_good, value >= good means good.
// For near_is_good, value <= good means good.
export const THRESHOLDS = {
  // ── Far-is-good (negative proximity) ─────────────────────────────
  residential_distance_m:    { good: 2000,  warn: 500,   bad: 0,     direction: 'far_is_good',
                                why: '> 2 km low noise impact; < 500 m typically unsiteable for 24/7 ops' },
  flood_zone_distance_m:     { good: 1000,  warn: 200,   bad: 0,     direction: 'far_is_good' },
  protected_area_distance_m: { good: 5000,  warn: 1000,  bad: 500,   direction: 'far_is_good',
                                why: 'NVE / Naturvernforbundet buffer expectations' },
  heritage_distance_m:       { good: 1000,  warn: 200,   bad: 0,     direction: 'far_is_good',
                                why: 'Museovirasto / Riksantikvaren typical excavation buffer' },
  refinery_distance_m:       { good: 10000, warn: 5000,  bad: 2000,  direction: 'far_is_good',
                                why: 'Seveso III blast / vapor cloud buffer' },
  chemical_distance_m:       { good: 5000,  warn: 2000,  bad: 1000,  direction: 'far_is_good' },
  power_plant_distance_m:    { good: 2000,  warn: 1000,  bad: 500,   direction: 'far_is_good',
                                why: 'Cooling water / thermal pollution / electrical noise' },
  industrial_distance_m:     { good: 1000,  warn: 500,   bad: 200,   direction: 'far_is_good' },
  agriculture_distance_m:    { good: 1000,  warn: 500,   bad: 200,   direction: 'far_is_good',
                                why: 'Odour / flies / disease vectors from intensive livestock' },
  fuel_storage_distance_m:   { good: 2000,  warn: 1000,  bad: 500,   direction: 'far_is_good' },
  gas_pipeline_distance_m:   { good: 500,   warn: 200,   bad: 50,    direction: 'far_is_good' },
  wastewater_distance_m:     { good: 1000,  warn: 500,   bad: 200,   direction: 'far_is_good' },

  // ── Near-is-good (positive proximity) ────────────────────────────
  grid_distance_km:      { good: 2,    warn: 5,    bad: 10,   direction: 'near_is_good',
                            why: 'Connection cost ~12 MNOK/km beyond 1 km' },
  road_distance_km:      { good: 1,    warn: 3,    bad: 10,   direction: 'near_is_good' },
  water_distance_km:     { good: 2,    warn: 10,   bad: 20,   direction: 'near_is_good' },
  ixp_distance_km:       { good: 50,   warn: 150,  bad: 300,  direction: 'near_is_good' },
  subsea_landing_km:     { good: 100,  warn: 300,  bad: 500,  direction: 'near_is_good' },
  cell_tower_distance_m: { good: 5000, warn: 15000, bad: 30000, direction: 'near_is_good' },
  dh_plant_distance_km:  { good: 5,    warn: 15,   bad: 30,   direction: 'near_is_good',
                            why: '< 5 km waste-heat economically usable; > 15 km not viable' },
};

// Returns 'good' | 'warn' | 'bad' | 'neutral'.
export function classifyDistance(fieldName, value) {
  if (value == null) return 'neutral';
  const rule = THRESHOLDS[fieldName];
  if (!rule) return 'neutral';
  if (rule.direction === 'far_is_good') {
    if (value >= rule.good) return 'good';
    if (value >= rule.warn) return 'warn';
    return 'bad';
  }
  if (value <= rule.good) return 'good';
  if (value <= rule.warn) return 'warn';
  return 'bad';
}

// Map bucket → CSS color name used by the existing ProjectsPage `c` callback.
export function bucketToColor(bucket) {
  if (bucket === 'good') return 'green';
  if (bucket === 'warn') return 'amber';
  if (bucket === 'bad')  return 'red';
  return 'gray';
}

// Human-readable explanation, used in tooltips. Returns null if no rule.
export function explainBucket(fieldName, value) {
  const rule = THRESHOLDS[fieldName];
  if (!rule || value == null) return null;
  const bucket = classifyDistance(fieldName, value);
  if (bucket === 'neutral') return null;
  const dir = rule.direction === 'far_is_good' ? '>' : '<';
  const goodCmp = rule.direction === 'far_is_good' ? `≥ ${rule.good}` : `≤ ${rule.good}`;
  const warnCmp = rule.direction === 'far_is_good' ? `≥ ${rule.warn}` : `≤ ${rule.warn}`;
  const txt = bucket === 'good' ? `good (${goodCmp})`
            : bucket === 'warn' ? `warn (${warnCmp}, but not ${goodCmp})`
            : `bad (${dir} ${rule.warn})`;
  return rule.why ? `${txt} — ${rule.why}` : txt;
}
